import React, { useEffect, useRef, useState, useCallback } from 'react';
import { apiFetch } from '../../utils/api';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const NATURE = {
  Cnergee: 'Branch-level internet/link & CBS issues across bank branches',
  izoologic: 'Recurring bi-weekly/monthly reports + user ID & asset changes',
  Sectona: 'PAM upgrades/version, launcher & login, user/asset mapping',
  'Infinity SDWAN': 'Branch link-down, faulty-device RMAs, port/config issues',
  'Manage Engine': 'Patching summaries / updates',
  'Active Directory': 'URL/wallpaper policy, domain implementation',
  'Airtel ISP': 'ISP link-down at branches',
  'MTNL ISP': 'ISP link issue',
  'Techowl SOC': 'SOC administrative',
};
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

// Build the full standalone report HTML (same design as the exported PDF).
function buildReportHtml(d, generatedOn) {
  const total = d.total || 1;
  const D = (pairs) => Object.fromEntries(pairs);
  const priD = D(d.byPriority), stD = D(d.byStatus);
  const [mY, mM] = d.month.split('-').map(Number);
  const monthName = `${MONTHS[mM - 1]} ${mY}`;
  const [topProd, topProdN] = d.byProduct[0] || ['—', 0];
  const [topCust, topCustN] = d.byCustomer[0] || ['—', 0];
  const [topAg, topAgN] = d.byAgent[0] || ['—', 0];
  const resolvedClosed = (stD.closed || 0) + (stD.resolved || 0);
  const openN = (stD.open || 0) + (stD.in_progress || 0);
  const bars = (pairs, color, denom) => {
    const base = denom || total;
    const mx = Math.max(...pairs.map(p => p[1]), 1);
    return pairs.map(([label, v]) =>
      `<div class="barrow"><div class="barlab"><span>${esc(label)}</span><span>${v} · ${Math.round(v / base * 100)}%</span></div>
       <div class="bartrack"><div class="barfill" style="width:${Math.max(v / mx * 100, 4)}%;background:${color}"></div></div></div>`).join('');
  };
  const prodRows = d.byProduct.map(([p, v]) =>
    `<tr><td><b>${esc(p)}</b></td><td>${v}</td><td>${Math.round(v / total * 100)}%</td><td>${esc(NATURE[p] || 'Mixed / isolated issues')}</td></tr>`).join('');
  const highNote = (priD.high || priD.critical)
    ? `<div class="note" style="background:#fef2f2;border-color:#fca5a5;color:#b91c1c"><b>Escalation watch:</b> ${priD.high || 0} high and ${priD.critical || 0} critical ticket(s) this month — review these first.</div>` : '';
  const spread = topAgN / total < 0.4 ? 'well spread across the team' : 'concentrated on one engineer (key-person risk)';

  return `<!doctype html><html lang="en"><head><meta charset="utf-8">
<title>Complaints Report — ${monthName}</title>
<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
:root{--kg:#0B6623;--navy:#0F1E3D;--t1:#0F172A;--t2:#64748B;--line:#E8ECF2;--bg:#F1F3F7;--raised:#2563EB;--amber:#b45309;}
*{box-sizing:border-box} body{margin:0;font-family:'Inter',system-ui,sans-serif;background:var(--bg);color:var(--t1);}
.wrap{max-width:900px;margin:0 auto;padding:0 0 40px;}
.hero{background:linear-gradient(135deg,#0F1E3D,#0B6623);color:#fff;padding:30px 40px;}
.hero .kick{font-size:12px;font-weight:700;letter-spacing:3px;color:#CADCFC;text-transform:uppercase;}
.hero h1{font-size:26px;margin:8px 0 4px;letter-spacing:-.5px;} .hero .sub{font-size:14px;color:#cdd9ee;}
.body{padding:0 40px;}
.kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin:-24px 0 24px;}
.kpi{background:#fff;border:1px solid var(--line);border-radius:14px;padding:16px 18px;box-shadow:0 6px 18px -8px rgba(16,24,40,.18);}
.kpi .n{font-size:26px;font-weight:800;letter-spacing:-1px;} .kpi .l{font-size:11.5px;color:var(--t2);font-weight:600;margin-top:4px;}
h2{font-size:17px;margin:26px 0 6px;letter-spacing:-.2px;} .lead{font-size:13.5px;color:var(--t2);margin:0 0 14px;line-height:1.6;}
.card{background:#fff;border:1px solid var(--line);border-radius:14px;padding:20px 22px;margin-bottom:18px;}
.grid2{display:grid;grid-template-columns:1fr 1fr;gap:18px;}
.barrow{margin-bottom:11px;} .barlab{display:flex;justify-content:space-between;font-size:12.5px;font-weight:600;margin-bottom:4px;} .barlab span:last-child{color:var(--t2);font-weight:700;}
.bartrack{background:#eef1f6;border-radius:6px;height:16px;overflow:hidden;} .barfill{height:100%;border-radius:6px;}
.find{display:flex;gap:12px;margin-bottom:12px;} .find .ic{font-size:18px;} .find .tx{font-size:13.5px;line-height:1.6;}
.note{background:#fffbeb;border:1px solid #fde68a;color:#92400e;border-radius:10px;padding:12px 14px;font-size:12.5px;margin-top:8px;}
.rec li{font-size:13.5px;line-height:1.7;margin-bottom:8px;}
table{width:100%;border-collapse:collapse;font-size:12.5px;} th{text-align:left;padding:8px 10px;background:#f6f8fb;color:var(--t2);font-size:11px;text-transform:uppercase;letter-spacing:.4px;border-bottom:1px solid var(--line);}
td{padding:7px 10px;border-bottom:1px solid var(--line);}
.foot{font-size:11px;color:var(--t2);padding:18px 40px;text-align:center;}
@media print{body{background:#fff}.kpi,.card{box-shadow:none}@page{margin:12mm}}
</style></head><body><div class="wrap">
<div class="hero"><div class="kick">KGreen Consulting &amp; Technologies Pvt Ltd · Support Analytics</div>
<h1>Monthly Complaints Report</h1><div class="sub">${monthName} · ${d.total} tickets logged · generated ${generatedOn}</div></div>
<div class="body">
<div class="kpis">
<div class="kpi"><div class="n">${d.total}</div><div class="l">Total complaints</div></div>
<div class="kpi"><div class="n" style="color:var(--kg)">${resolvedClosed}</div><div class="l">Resolved / closed</div></div>
<div class="kpi"><div class="n" style="color:var(--amber)">${openN}</div><div class="l">Open / in progress</div></div>
<div class="kpi"><div class="n" style="color:var(--raised)">${d.avgResolutionHrs == null ? '—' : d.avgResolutionHrs + 'h'}</div><div class="l">Avg resolution</div></div>
</div>
${d.total === 0 ? '<div class="card">No complaints were logged in this month.</div>' : `
<h2>Executive summary</h2>
<p class="lead">In ${monthName}, support logged <b>${d.total} complaints</b>. The largest product by volume was <b>${esc(topProd)}</b> with <b>${topProdN}</b> tickets (~${Math.round(topProdN / total * 100)}%), and the heaviest client was <b>${esc(topCust)}</b> (${topCustN} tickets). Severity: ${priD.critical || 0} critical, ${priD.high || 0} high, ${priD.medium || 0} medium, ${priD.low || 0} low — with ${d.resolved} resolved in an average of ${d.avgResolutionHrs == null ? 'n/a' : d.avgResolutionHrs + ' hours'}. ${resolvedClosed} of ${d.total} are resolved/closed; ${openN} remain open or in progress.</p>
${highNote}
<div class="grid2">
<div class="card"><h2 style="margin-top:0">Which products had issues</h2><p class="lead">Tickets by product.</p>${bars(d.byProduct, '#0B6623')}</div>
<div class="card"><h2 style="margin-top:0">Where complaints came from</h2><p class="lead">Top clients by volume.</p>${bars(d.byCustomer.slice(0, 8), '#2563EB')}</div>
</div>
<div class="card"><h2 style="margin-top:0">Nature &amp; severity</h2><div class="grid2">
<div><div style="font-size:12px;font-weight:700;color:var(--t2);margin-bottom:8px">BY PRIORITY</div>${bars(d.byPriority, '#d97706')}
<div style="font-size:12px;font-weight:700;color:var(--t2);margin:14px 0 8px">BY CATEGORY</div>${bars(d.byCategory, '#6d28d9')}</div>
<div><div style="font-size:12px;font-weight:700;color:var(--t2);margin-bottom:8px">BY STATUS</div>${bars(d.byStatus, '#0d7a28')}
<div style="font-size:12px;font-weight:700;color:var(--t2);margin:14px 0 8px">BY AGENT (WORKLOAD)</div>${bars(d.byAgent, '#1C7293')}</div>
</div></div>
${(d.resolution && d.resolution.count) ? `
<h2>Resolution analysis</h2>
<p class="lead">Based on the ${d.resolution.count} ticket${d.resolution.count === 1 ? '' : 's'} with a recorded resolution this month.</p>
<div class="grid2">
<div class="card"><h2 style="margin-top:0">Outcomes</h2>${bars(d.resolution.byOutcome, '#0B6623', d.resolution.count)}
<div style="display:flex;gap:10px;margin-top:12px">
  <span class="pill" style="background:#eff4ff;color:var(--raised)">Customer-confirmed ${d.resolution.confirmedPct}%</span>
  <span class="pill" style="background:#fbeedd;color:var(--amber)">RMAs ${d.resolution.rmaCount}</span>
</div></div>
<div class="card"><h2 style="margin-top:0">Top root causes</h2>${d.resolution.topRootCauses.length ? bars(d.resolution.topRootCauses, '#6d28d9', d.resolution.count) : '<p class="lead" style="margin:0">No root causes recorded yet.</p>'}</div>
</div>` : ''}
<h2>Key findings</h2>
<div class="card">
<div class="find"><div class="ic">📦</div><div class="tx"><b>Top product: ${esc(topProd)} (${Math.round(topProdN / total * 100)}% of tickets).</b> ${esc(NATURE[topProd] || 'Concentrated issue stream')}.</div></div>
<div class="find"><div class="ic">🏢</div><div class="tx"><b>Top client: ${esc(topCust)} (${topCustN} tickets).</b> Complaint volume is concentrated here; the rest spreads across many parties with few tickets each.</div></div>
<div class="find"><div class="ic">👤</div><div class="tx"><b>Workload.</b> ${esc(topAg)} handled ${topAgN} of ${d.total} tickets; load is ${spread}.</div></div>
<div class="find"><div class="ic">⚡</div><div class="tx"><b>Severity &amp; speed.</b> ${priD.critical || 0} critical / ${priD.high || 0} high; ${d.resolved} resolved at avg ${d.avgResolutionHrs == null ? 'n/a' : d.avgResolutionHrs + 'h'}. ${openN} still open/in-progress — worth an ageing review.</div></div>
</div>
<h2>Recommendations</h2>
<div class="card"><ol class="rec">
<li><b>Track the ${esc(topProd)} / ${esc(topCust)} stream as a Project</b> (Gantt + milestones) rather than many individual tickets.</li>
<li><b>Pre-empt recurring asks</b> (periodic reports, upgrades, ISP/branch link-downs) with KB articles and standing tasks so they don't re-open each cycle.</li>
<li><b>Clear the ${openN} open/in-progress tickets</b> that are ageing; keep severity & category tagging accurate.</li>
<li><b>Balance workload</b> so no single engineer carries a disproportionate share.</li>
</ol></div>
<h2>Product issue detail</h2>
<div class="card"><table><thead><tr><th>Product</th><th>Tickets</th><th>Share</th><th>Nature of issues</th></tr></thead><tbody>${prodRows}</tbody></table></div>
`}
</div>
<div class="foot">KGreen Consulting &amp; Technologies Pvt Ltd · Confidential · Source: Support Portal tickets, ${monthName}</div>
</div></body></html>`;
}

export default function MonthlyReport() {
  const today = new Date();
  // default to last complete month
  const def = new Date(today.getFullYear(), today.getMonth(), 1);
  const [year, setYear] = useState(def.getFullYear());
  const [month, setMonth] = useState(def.getMonth() + 1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [html, setHtml] = useState('');
  const frameRef = useRef(null);
  const years = []; for (let y = today.getFullYear() - 4; y <= today.getFullYear(); y++) years.push(y);

  const load = useCallback(() => {
    setLoading(true); setError('');
    apiFetch(`/analytics/complaints-report?year=${year}&month=${month}`)
      .then(d => setHtml(buildReportHtml(d, today.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }))))
      .catch(e => { setError(e.message); setHtml(''); })
      .finally(() => setLoading(false));
  }, [year, month]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  function downloadPdf() {
    const frame = frameRef.current;
    if (!frame) return;
    try {
      frame.contentWindow.focus();
      frame.contentWindow.print();
    } catch (_) {
      // fallback: open in a new window and print
      const w = window.open('', '_blank');
      if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 400); }
    }
  }

  const sel = { padding: '9px 32px 9px 12px', border: '1.5px solid var(--n-300)', borderRadius: 10, fontSize: 13.5, fontWeight: 600, background: 'var(--bg-surface)', color: 'var(--text-primary)' };

  return (
    <div className="page" style={{ padding: '26px 30px', maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 18 }}>
        <div>
          <div className="page-title">Monthly Complaints Report</div>
          <div className="page-sub">Pick a month, then download a ready-to-circulate PDF</div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <select className="input" style={sel} value={month} onChange={e => setMonth(+e.target.value)}>
            {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select className="input" style={sel} value={year} onChange={e => setYear(+e.target.value)}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button className="btn btn-primary" onClick={downloadPdf} disabled={loading || !html} style={{ whiteSpace: 'nowrap' }}>⬇ Download PDF</button>
        </div>
      </div>

      {loading && <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Loading report…</div>}
      {error && <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', color: '#b91c1c', borderRadius: 10, padding: '12px 16px', fontSize: 13 }}>Could not load report: {error}</div>}

      {!loading && !error && (
        <div className="card" style={{ padding: 0, overflow: 'hidden', border: '1px solid var(--border-subtle)' }}>
          <iframe
            ref={frameRef}
            title="report-preview"
            srcDoc={html}
            style={{ width: '100%', height: '78vh', border: 'none', display: 'block', background: '#fff' }}
          />
        </div>
      )}
      <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 10 }}>
        Tip: in the print dialog, choose <b>Save as PDF</b> as the destination. The current month shows data up to today.
      </p>
    </div>
  );
}
